import {
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { RuleService } from '../../../services/rule.service';
import { StageDistribution } from '../../../models/stage-distribution.model';
import { ChartData, ChartOptions } from 'chart.js';
import { getStageLabel } from '../../../../utils/enum-label.utils';
import { Stage } from '../../../models/rule.model';

@Component({
  selector: 'app-stage-distribution-chart',
  templateUrl: './stage-distribution-chart.component.html',
  styleUrls: ['./stage-distribution-chart.component.scss'],
  standalone: false,
})
export class StageDistributionChartComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() filterPayload: any;

  chartData?: ChartData<'bar'>;
  chartOptions: ChartOptions<'bar'>;
  loading = false;
  private destroy$ = new Subject<void>();

  constructor(
    private ruleService: RuleService,
    private cdr: ChangeDetectorRef,
  ) {
    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const value = context.parsed.y ?? 0;
              return `${context.label}: ${value} règle${value > 1 ? 's' : ''}`;
            },
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            stepSize: 1,
          },
        },
      },
    };
  }

  ngOnInit(): void {
    if (this.filterPayload) {
      this.loadStageDistribution();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filterPayload'] && !changes['filterPayload'].firstChange) {
      this.loadStageDistribution();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadStageDistribution(): void {
    if (!this.filterPayload) return;

    this.loading = true;

    this.ruleService
      .GetStageDistribution(this.filterPayload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: StageDistribution) => {
          this.updateChart(data);
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error loading stage distribution:', error);
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }

  private updateChart(data: StageDistribution): void {
    const labels = [
      getStageLabel(Stage.Dev) as string,
      getStageLabel(Stage.Qa) as string,
      getStageLabel(Stage.ProdManual) as string,
      getStageLabel(Stage.ProdAutomated) as string,
    ];

    const values = [data.dev, data.qa, data.prodManual, data.prodAutomated];

    const backgroundColors = [
      'rgba(59, 130, 246, 0.7)', // Blue for Dev
      'rgba(234, 179, 8, 0.7)', // Yellow for Qa
      'rgba(34, 197, 94, 0.7)', // Green for ProdManual
      'rgba(22, 163, 74, 0.7)', // Darker Green for ProdAutomated
    ];

    const borderColors = [
      'rgb(59, 130, 246)',
      'rgb(234, 179, 8)',
      'rgb(34, 197, 94)',
      'rgb(22, 163, 74)',
    ];

    this.chartData = {
      labels: labels,
      datasets: [
        {
          data: values,
          backgroundColor: backgroundColors,
          borderColor: borderColors,
          borderWidth: 1,
        },
      ],
    };
  }
}
